import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import { adminApi } from '../../services/admin';

export default function AdminDashboard() {
  const queryClient = useQueryClient();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: adminApi.getStats,
  });

  const runScoring = useMutation({
    mutationFn: adminApi.runScoring,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const cycle = stats?.last_scoring_cycle;

  return (
    <>
      <PageHeader
        title="Admin Dashboard"
        description="System overview and management"
        actions={
          <button
            onClick={() => {
              if (window.confirm('Run manual scoring for the current week? This will score all guards, sites, and vendors.')) {
                runScoring.mutate();
              }
            }}
            disabled={runScoring.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
          >
            {runScoring.isPending ? 'Running...' : 'Run Manual Scoring'}
          </button>
        }
      />

      {runScoring.isError && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">Scoring run failed. Check server logs.</p>
        </div>
      )}
      {runScoring.isSuccess && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm text-emerald-600">Scoring completed successfully.</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {[
          { label: 'Vendors', value: stats?.total_vendors ?? 0 },
          { label: 'Clients', value: stats?.total_clients ?? 0 },
          { label: 'Sites', value: stats?.total_sites ?? 0 },
          { label: 'Guards', value: stats?.total_guards ?? 0 },
          { label: 'Users', value: stats?.total_users ?? 0 },
          { label: 'Active Rules', value: stats?.active_rules ?? 0 },
          { label: 'Total Rules', value: stats?.total_rules ?? 0 },
          { label: 'Uploads', value: stats?.total_uploads ?? 0 },
          { label: 'Parsed Events', value: stats?.total_parsed_events ?? 0 },
          { label: 'Scoring Cycles', value: stats?.total_scoring_cycles ?? 0 },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-slate-100 bg-white p-4">
            <p className="text-xs font-medium text-slate-400 uppercase">{s.label}</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Last Scoring Cycle */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Last Scoring Cycle</h2>
        {cycle ? (
          <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Week</dt>
              <dd className="mt-1 text-sm text-slate-900">{cycle.week_start} — {cycle.week_end}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Status</dt>
              <dd className="mt-1">
                <StatusBadge
                  label={cycle.status}
                  variant={cycle.status === 'completed' ? 'success' : cycle.status === 'running' ? 'warning' : 'error'}
                />
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Triggered By</dt>
              <dd className="mt-1 text-sm text-slate-900 capitalize">{cycle.triggered_by}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Completed</dt>
              <dd className="mt-1 text-sm text-slate-900">
                {cycle.completed_at ? new Date(cycle.completed_at).toLocaleString() : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Guards Scored</dt>
              <dd className="mt-1 text-sm text-slate-900">{cycle.total_guards_scored}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Sites Scored</dt>
              <dd className="mt-1 text-sm text-slate-900">{cycle.total_sites_scored}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Rules Triggered</dt>
              <dd className="mt-1 text-sm text-slate-900">{cycle.total_rules_triggered} / {cycle.total_rules_evaluated}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Vendors Scored</dt>
              <dd className="mt-1 text-sm text-slate-900">{cycle.total_vendors_scored}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-slate-400">No scoring cycles have run yet.</p>
        )}
      </div>
    </>
  );
}
